"use client";

import Link from "next/link";
import { useState } from "react";
import { Img } from "@/components/image";
import { buttonVariants } from "@/components/ui/button";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { DietHubSectionHeader } from "@/features/hubs/components/diet-hub-section-header";
import type { DietHubContentBenefitsSectionsType } from "@/features/hubs/types/diet-hub.type";
import { useCarouselDrag } from "@/lib/hooks/use-carousel-drag";
import { strapiUrl } from "@/lib/strapi";
import { cn } from "@/lib/utils";

type DietHubBenefitItem = NonNullable<
  DietHubContentBenefitsSectionsType["benefits"]
>[number];

const DietHubBenefitCard = ({ benefit }: { benefit: DietHubBenefitItem }) => {
  const iconAttrs = benefit.icon;
  const iconSrc =
    iconAttrs?.url !== undefined && iconAttrs.url !== ""
      ? strapiUrl(iconAttrs.url)
      : null;

  return (
    <div className={cn("space-y-4 rounded-xl border border-border p-6")}>
      {iconSrc ? (
        <Img
          alt={iconAttrs?.alternativeText ?? ""}
          className="size-16 shrink-0 object-cover"
          height={iconAttrs?.height}
          src={iconSrc}
          width={iconAttrs?.width}
        />
      ) : null}
      <div>
        <header className="flex flex-col items-start gap-4">
          <h3 className="font-medium text-brand-green1-300 text-xl">
            {benefit.title}
          </h3>
        </header>
        <p className="text-base text-brand-green1-300">{benefit.description}</p>
      </div>
      {benefit.callbackUrl && benefit.callbackLabel && (
        <Link
          className={cn(
            buttonVariants({ variant: "link" }),
            "min-h-0 px-0 text-current text-sm",
          )}
          href={benefit.callbackUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          {benefit.callbackLabel}
        </Link>
      )}
    </div>
  );
};

export const DietHubBenefits = ({
  content,
}: {
  content: DietHubContentBenefitsSectionsType;
}) => {
  const benefits = content.benefits ?? [];

  const [api, setApi] = useState<CarouselApi>();

  const isDragging = useCarouselDrag(api);

  return (
    <section className="py-12">
      <div className="container">
        <div className="space-y-8">
          <DietHubSectionHeader
            label={content.label}
            title={content.title}
            callback={content.callback}
            description={content.description}
          />
          {benefits.length > 0 && (
            <>
              <div
                className={
                  'relative after:absolute after:top-0 after:right-0 after:h-full after:w-14 after:bg-linear-to-r after:from-transparent after:to-white after:content-[""] md:hidden'
                }
              >
                <Carousel
                  opts={{
                    align: "start",
                  }}
                  setApi={setApi}
                >
                  <CarouselContent className="last:mr-10">
                    {benefits.map((benefit) => (
                      <CarouselItem className="basis-[88%]" key={benefit.title}>
                        <DietHubBenefitCard benefit={benefit} />
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {!isDragging && (
                    <>
                      <CarouselPrevious className="left-0 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:-left-4" />
                      <CarouselNext className="right-4 z-10 size-12 rounded-md border-border drop-shadow-[2px_12px_7px_hsla(0,0%,0%,0.06)] hover:bg-background/90 disabled:invisible sm:right-10" />
                    </>
                  )}
                </Carousel>
              </div>
              <div className="hidden gap-4 md:grid md:grid-cols-2 lg:grid-cols-4">
                {benefits.map((benefit) => (
                  <DietHubBenefitCard benefit={benefit} key={benefit.title} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};
